// src/domains/auth/pages/AccessBlockedScreen.jsx
// Tela cheia exibida quando o acesso está bloqueado (trial vencido / plano expirado)
// ou suspenso manualmente pelo super admin. Não exibe sidebar nem navegação.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, SUPER_ADMIN_EMAIL } from 'contexts/AuthContext';

const T = {
  brand: '#6B5876', accent: '#C9929A', border: '#E8E5EC',
  s2: '#FAF7FB', s3: '#F0EDF3', text: '#3D3447', text2: '#6B6070', muted: '#A8A1B0',
  warnBg: '#FFF8F0', warnFg: '#8B6914', warnBorder: '#FBBF24',
  sans: "'DM Sans',sans-serif", serif: "'Instrument Serif',serif",
};

const fmtDate = (ts) => {
  const d = ts?.toDate?.() ?? null;
  return d ? d.toLocaleDateString('pt-BR') : null;
};

// Quantos dias se passaram desde o vencimento (≥ 0). Null se sem data.
const daysSince = (ts) => {
  const d = ts?.toDate?.() ?? null;
  if (!d) return null;
  return Math.max(0, Math.floor((new Date() - d) / 86400000));
};

export default function AccessBlockedScreen() {
  const { userProfile, effectiveAccessStatus, logout } = useAuth();
  const navigate = useNavigate();

  const nome      = userProfile?.name || 'Administrador';
  const isSuspended = effectiveAccessStatus === 'suspended';
  const expDate   = fmtDate(userProfile?.accessExpiresAt);
  const since     = daysSince(userProfile?.accessExpiresAt);

  const handleLogout = async () => {
    try { await logout(); } catch { /* ignora */ }
    navigate('/login', { replace: true });
  };

  const mailtoAtivacao = `mailto:${SUPER_ADMIN_EMAIL}?subject=${encodeURIComponent(
    `Ativação de plano — ${nome}`
  )}`;
  const mailtoSuporte = `mailto:${SUPER_ADMIN_EMAIL}?subject=${encodeURIComponent(
    `Conta suspensa — ${nome}`
  )}`;

  return (
    <div style={{ minHeight: '100vh', background: T.s2, fontFamily: T.sans,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '24px 16px' }}>

      {/* Logo */}
      <div style={{ fontFamily: T.serif, fontSize: 30, color: T.brand, marginBottom: 24 }}>
        Agenda<span style={{ color: '#B5A2D2' }}>Pro</span>
      </div>

      <div style={{ width: '100%', maxWidth: 460, background: '#fff', borderRadius: 18,
        boxShadow: '0 8px 32px rgba(107,88,118,0.10)', padding: '32px 28px', textAlign: 'center' }}>

        {isSuspended ? (
          // ── Conta suspensa ────────────────────────────────────────────────
          <>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontFamily: T.serif, fontSize: 24, color: T.text, marginBottom: 10 }}>
              Conta temporariamente suspensa
            </div>
            <p style={{ fontSize: 14, color: T.text2, lineHeight: 1.65, marginBottom: 24 }}>
              Olá, <strong>{nome}</strong>. O acesso à sua conta foi suspenso pelo
              administrador da plataforma. Para mais informações e para regularizar
              sua situação, entre em contato:
            </p>
          </>
        ) : (
          // ── Trial vencido / plano expirado ────────────────────────────────
          <>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⏰</div>
            <div style={{ fontFamily: T.serif, fontSize: 24, color: T.text, marginBottom: 10 }}>
              Seu período de teste chegou ao fim
            </div>
            <p style={{ fontSize: 14, color: T.text2, lineHeight: 1.65, marginBottom: 8 }}>
              Olá, <strong>{nome}</strong>! Seu acesso ao AgendaPro
              {expDate ? <> encerrou em <strong>{expDate}</strong></> : ' chegou ao fim'}.
            </p>
            {since != null && since > 0 && (
              <div style={{ display: 'inline-block', fontSize: 12, fontWeight: 600,
                color: '#9a3412', background: '#FEF0E8', borderRadius: 20,
                padding: '4px 14px', marginBottom: 16 }}>
                venceu há {since} {since === 1 ? 'dia' : 'dias'}
              </div>
            )}
            <p style={{ fontSize: 14, color: T.text2, lineHeight: 1.65, marginBottom: 24 }}>
              Para continuar usando o sistema sem interrupções, entre em contato com o
              administrador da plataforma e escolha o plano ideal para a sua clínica.
            </p>
          </>
        )}

        {/* Botões */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
          <a href={isSuspended ? mailtoSuporte : mailtoAtivacao}
            style={{ display: 'block', padding: '13px 16px', background: T.brand, color: '#fff',
              borderRadius: 12, fontSize: 14, fontWeight: 600, textDecoration: 'none',
              fontFamily: T.sans }}>
            📧 Falar com o Administrador →
          </a>
          <button onClick={handleLogout}
            style={{ padding: '12px 16px', background: '#fff', color: T.text2,
              border: `1.5px solid ${T.border}`, borderRadius: 12, fontSize: 14,
              cursor: 'pointer', fontFamily: T.sans }}>
            Sair da Conta
          </button>
        </div>

        {/* Bloco de contato */}
        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 18,
          fontSize: 13, color: T.text2 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.muted, textTransform: 'uppercase',
            letterSpacing: 1, marginBottom: 10 }}>
            Administrador da plataforma
          </div>
          <div style={{ marginBottom: 4 }}>📧 {SUPER_ADMIN_EMAIL}</div>
          <div style={{ color: T.muted }}>⏰ Atendimento em até 1 dia útil</div>
        </div>

        {!isSuspended && (
          <p style={{ fontSize: 12, color: T.muted, marginTop: 18, lineHeight: 1.5 }}>
            Seus dados estão seguros e serão mantidos por 30 dias após o vencimento.
          </p>
        )}
      </div>
    </div>
  );
}
